import { Sentry } from "@/lib/sentry";

/**
 * #1409 — read the message off whatever a catch block caught.
 *
 * supabase-js returns PostgrestError as a plain object, not an Error, so an
 * `instanceof Error` check showed the fixed fallback text for every database
 * failure and hid the real cause (RLS denial, PGRST code, network error).
 * Anything carrying a non-empty string `message` counts.
 */
export function getErrorMessage(err: unknown, fallback: string): string {
  if (typeof err === "string") return err.trim() ? err : fallback;
  if (err && typeof err === "object") {
    const message = (err as { message?: unknown }).message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
}

/**
 * Report a caught failure to Sentry. `message` is the text the user was shown,
 * which for an edge-function failure differs from the Error's own fixed
 * `.message`, so it always goes in `extra`.
 */
export function captureError(err: unknown, action: string, message: string): void {
  const isObject = !!err && typeof err === "object";
  const e = isObject ? (err as Record<string, unknown>) : {};
  const code = typeof e.code === "string" ? e.code : undefined;
  // A constraint failure's details echo the whole row (client contact info).
  const details = code?.startsWith("23") ? "[redacted: row data]" : e.details;
  Sentry.captureException(err instanceof Error ? err : new Error(message), {
    tags: { action },
    fingerprint: ["caught-error", action, code ?? "no-code"],
    extra: {
      message,
      code,
      details,
      hint: e.hint,
      ...(isObject ? {} : { raw: String(err) }),
    },
  });
}
